// src/pages/vehicle/vehicleupload.tsx
import React, { useCallback, useMemo, useRef, useState } from "react";
import Webcam from "react-webcam";
import "../../../../helper/camera/ClCamera.css";
import axios from "axios";
import {
  Button,
  Card,
  CardContent,
  Container,
  Typography,
  Grid,
} from "@mui/material";
import Header, { HeaderType } from "../../../../components/Header";
import { useNavigate, useSearchParams } from "react-router-dom";
import PublishIcon from "@mui/icons-material/Publish";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import DeleteIcon from "@mui/icons-material/Delete";
import UploadIcon from "@mui/icons-material/Upload";
import { environment } from "../../../../environment/environment";
import { useConfirm } from "@/components/dialog/confirm";
import BrandLoader from "@/branding/BrandLoader";
import { useAuth, getAccessToken } from "@/context/auth";
import { ScanMethodEnum } from "@/services/model/scanLicenseDiskDocumentResponse";

import {
  CONSTRAINTS_1080P,
  normalizeForUpload,
  takeStillPhotoFromTrack,
  scaleToMax1080,
  upgradeVideoTrackQuality,
} from "../../../../helper/camera/webcam-utils";

/* ----------------------------- component ----------------------------- */

interface ScannerProps {
  style?: any;
  loading: boolean;
  offline: boolean;
}

type Stage = "idle" | "camera" | "preview"; // idle = choose buttons, camera = live view (desktop), preview = still/photo chosen

const Scanner: React.FC<ScannerProps> = (props) => {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const returnUrl = sp.get("return"); // NEW

  const confirm = useConfirm();
  const { authTokens } = useAuth();
  const token = getAccessToken(authTokens) ?? "";

  const [stage, setStage] = useState<Stage>("idle");
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);

  const webcamRef = useRef<Webcam | null>(null);

  // separate inputs so "Upload from device" and "Open camera" behave correctly
  const filePickerRef = useRef<HTMLInputElement | null>(null); // gallery/files
  const cameraPickerRef = useRef<HTMLInputElement | null>(null); // native camera

  const isMobile = useMemo(
    () => /Android|iPhone|iPad|iPod/i.test(navigator.userAgent),
    []
  );
  const [useNativeCamera, setUseNativeCamera] = useState<boolean>(isMobile);

  const goBack = () => {
    if (returnUrl) {
      navigate(returnUrl, { replace: true });
    } else {
      navigate(-1);
    }
  };

  const showConfirmDialog = (
    title: string,
    message: unknown,
    custom: boolean = false
  ) => {
    const text =
      typeof message === "string"
        ? message
        : message instanceof Error
        ? message.message
        : JSON.stringify(message, null, 2);

    if (!custom) {
      confirm({
        catchOnCancel: false,
        title,
        description: text,
      });
    } else {
      confirm({
        catchOnCancel: true,
        title,
        description: text,
        okOnly: false,
        confirmText: "Yes",
        cancelText: "No",
      })
        .then(() => {
          goBack();
          discardImage();
        })
        .catch(async () => {
          // no-op (DeleteVehicle code is commented out)
        });
    }
  };

  /* -------- native file flows -------- */
  const openDevicePicker = () => filePickerRef.current?.click(); // gallery/doc picker
  const openNativeCameraPicker = () => cameraPickerRef.current?.click(); // camera capture

  const onPickedFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const dataUrl = await new Promise<string>((res, rej) => {
      const r = new FileReader();
      r.onload = () => res(String(r.result));
      r.onerror = rej;
      r.readAsDataURL(f);
    });
    const scaled1080p = await scaleToMax1080(dataUrl);
    setCapturedImage(scaled1080p);
    setStage("preview");
    e.target.value = ""; // allow re-pick of same file
  };

  /* -------- webcam ready/error (desktop only) -------- */
  const handleUserMediaError = useCallback(() => {
    // If desktop webcam fails/blocked, fall back to native
    setUseNativeCamera(true);
    openNativeCameraPicker();
  }, []);

  const handleUserMediaOk = useCallback(() => {
    setUseNativeCamera(false);
    upgradeVideoTrackQuality(webcamRef);
  }, []);

  /* -------- actions -------- */
  const startCamera = () => {
    // Desktop-only live camera
    setUseNativeCamera(false);
    setStage("camera");
  };

  const startUploadFromDevice = () => openDevicePicker();

  const startOpenNativeCamera = () => openNativeCameraPicker();

  const captureFromCamera = async () => {
    // Desktop webcam flow
    if (useNativeCamera || !webcamRef.current) {
      openNativeCameraPicker();
      return;
    }

    let dataUrl = await takeStillPhotoFromTrack(webcamRef);

    if (!dataUrl) {
      dataUrl = (webcamRef.current as any)?.getScreenshot({
        width: 1920,
        height: 1080,
      }) as string | undefined;
    }

    if (!dataUrl) {
      setUseNativeCamera(true);
      openNativeCameraPicker();
      return;
    }

    const scaled1080p = await scaleToMax1080(dataUrl);
    setCapturedImage(scaled1080p);
    setStage("preview");
  };

  const discardImage = () => {
    setCapturedImage(null);
    setStage("idle");
  };

  const uploadImage = async () => {
    try {
      if (!capturedImage) {
        showConfirmDialog(
          "Missing image",
          "Please capture or pick a photo first."
        );
        return;
      }

      setLoading(true);

      if (props.offline) {
        const key = `digialert_pwa_${Math.random().toString(36).slice(2, 7)}`;
        localStorage.setItem(key, capturedImage);
        showConfirmDialog(
          "Image saved locally!",
          "You're offline. We'll upload once reconnected."
        );
        discardImage();
        setLoading(false);
        return;
      }

      const storedValue = window.localStorage.getItem("dynamicViewVehicleId");
      const dynamicViewVehicleId: string = storedValue
        ? String(JSON.parse(storedValue))
        : "00000000-0000-0000-0000-000000000000";

      const { base64, type } = await normalizeForUpload(capturedImage);
      const documents = {
        linkedId: dynamicViewVehicleId,
        fileName: `digialert_pwa_`,
        file: base64, // no data: prefix
        fileType: type, // "jpeg"
        documentTypeId: "00000000-0000-0000-0000-000000000003",
        formId: null,
      };

      const config = { headers: { Authorization: `Bearer ${token}` } };
      setUploading(true);

      const response = await axios.post(
        `${environment.baseUrl}/document/scanLicenseDisc/`,
        documents,
        { ...config, params: { generic: true } }
      );

      checkUploadStatus(response);
    } catch (err) {
      setUploading(false);
      setLoading(false);
      showConfirmDialog(
        "Failed to upload image!",
        "We were unable to upload your image."
      );
      if (err instanceof Error) console.error(err.message);
    }
  };

  const checkUploadStatus = (response: any) => {
    setUploading(false);
    setLoading(false);

    if (response && response.data.data.vehicleId) {
      localStorage.setItem(
        "dynamicViewVehicleId",
        JSON.stringify(response.data.data.vehicleId)
      );
      localStorage.setItem("dynamicVehicleScanUpdatedAt", Date.now().toString());
    }

    const BARCODE_TIPS_TITLE = "Tips for a successful barcode scan";
    const BARCODE_TIPS_BODY = [
      "Place the license disc on a flat surface in good, even lighting.",
      "Hold your phone steady and keep the barcode fully inside the frame.",
      "Move closer until the barcode is sharp and readable (no blur).",
      "Avoid glare or reflections — tilt the disc slightly if light is bouncing off it.",
      "Make sure the barcode is not folded, scratched, or covered.",
    ]
      .map((t) => `• ${t}`)
      .join("\n");

    if (response.status === 200 && response.data?.data?.success) {
      if (response.data.data.scanMethod !== ScanMethodEnum.Barcode) {
        // OCR scan -> treated as failure
        showConfirmDialog(
          "We couldn’t read the barcode clearly",
          `The image uploaded successfully, but the barcode was too unclear to scan.
 
           ${BARCODE_TIPS_TITLE}
 
           ${BARCODE_TIPS_BODY}
 
           Would you like to capture your details manually?`,
          true
        );
      } else {
        // success
        goBack();
        discardImage();
      }
    } else {
      if (response.data.data === "Duplicate License Disc") {
        showConfirmDialog(
          "This license disc is already added",
          "It looks like you already added this vehicle. If this is a different vehicle, please double-check that you’re scanning the correct disc."
        );
      } else {
        showConfirmDialog(
          "Upload worked, but barcode scan failed",
          `We couldn’t detect a readable barcode from your photo.
           
           ${BARCODE_TIPS_TITLE}
 
           • ${BARCODE_TIPS_BODY}
           If you still have trouble, try taking the photo in brighter light or cleaning the disc.
 
           Would you like to capture your details manually?`,
          true
        );
      }
    }
  };

  /* -------- button layouts (matches your other screen) -------- */
  const ButtonsIdle = (
    <Grid container spacing={1}>
      <Grid size={12 as any}>
        <Button
          variant="outlined"
          color="primary"
          className="captureButton"
          onClick={startUploadFromDevice}
          startIcon={<UploadIcon />}
          fullWidth
          size="large"
        >
          Upload from device
        </Button>
      </Grid>
      <Grid size={12 as any}>
        <Button
          variant="contained"
          color="secondary"
          className="captureButton"
          onClick={isMobile ? startOpenNativeCamera : startCamera}
          startIcon={<CameraAltIcon />}
          fullWidth
          size="large"
        >
          {isMobile ? "Open camera" : "Open camera (desktop)"}
        </Button>
      </Grid>
    </Grid>
  );

  const ButtonsCamera = (
    <Grid container spacing={1}>
      <Grid size={12 as any}>
        <Button
          variant="contained"
          color="secondary"
          className="captureButton"
          onClick={captureFromCamera}
          startIcon={<CameraAltIcon />}
          fullWidth
          size="large"
        >
          Capture Image
        </Button>
      </Grid>
      <Grid size={12 as any}>
        <Button
          variant="outlined"
          color="primary"
          className="deleteButton"
          onClick={() => setStage("idle")}
          fullWidth
          size="large"
        >
          Cancel
        </Button>
      </Grid>
    </Grid>
  );

  const ButtonsPreview = (
    <Grid container spacing={1}>
      <Grid size={12 as any}>
        <Button
          variant="contained"
          color="primary"
          className="captureButton"
          onClick={uploadImage}
          disabled={loading}
          startIcon={<PublishIcon />}
          fullWidth
          size="large"
        >
          Accept
        </Button>
        <Button
          variant="outlined"
          color="secondary"
          className="deleteButton"
          onClick={discardImage}
          startIcon={<DeleteIcon />}
          fullWidth
          disabled={loading}
          size="large"
          sx={{ mt: 1 }}
        >
          Recapture
        </Button>
      </Grid>
    </Grid>
  );

  return (
    <>
      <Header type={HeaderType.Scanner} />
      <Container maxWidth="sm">
        <Card variant="outlined">
          <CardContent>
            <Grid container spacing={1}>
              <Grid size={12 as any}>
                <Grid container justifyContent="center">
                  <Typography variant="caption">
                    Please capture an image of the vehicle&apos;s license disk
                  </Typography>
                </Grid>
              </Grid>

              <Grid container spacing={1}>
                <Grid size={12 as any}>
                  <Grid container justifyContent="center">
                    {/* Desktop live camera only when stage === 'camera' */}
                    {!isMobile &&
                      stage === "camera" &&
                      !capturedImage &&
                      !useNativeCamera && (
                        <div className="imageStage">
                          <Webcam
                            ref={webcamRef}
                            audio={false}
                            mirrored={false}
                            playsInline
                            screenshotFormat="image/jpeg"
                            screenshotQuality={1}
                            videoConstraints={CONSTRAINTS_1080P}
                            onUserMedia={handleUserMediaOk}
                            onUserMediaError={handleUserMediaError}
                            className="cameraVideo"
                          />
                        </div>
                      )}

                    {/* Captured/selected preview */}
                    {stage === "preview" && capturedImage && (
                      <div className="imageStage">
                        <img
                          src={capturedImage}
                          alt="captured"
                          className="cameraPreview"
                          style={{
                            width: "100%",
                            height: "auto",
                            objectFit: "contain",
                            borderRadius: 8,
                          }}
                        />
                      </div>
                    )}

                    {/* Hidden native inputs (always mounted) */}
                    {/* Upload from device (gallery) */}
                    <input
                      ref={filePickerRef}
                      type="file"
                      accept="image/*"
                      multiple={false}
                      style={{ display: "none" }}
                      onChange={onPickedFile}
                    />
                    {/* Open camera (native) */}
                    <input
                      ref={cameraPickerRef}
                      type="file"
                      accept="image/*"
                      capture="environment"
                      multiple={false}
                      style={{ display: "none" }}
                      onChange={onPickedFile}
                    />
                  </Grid>
                </Grid>

                <Grid size={12 as any}>
                  <Grid container justifyContent="center">
                    {stage === "idle" && ButtonsIdle}
                    {stage === "camera" && ButtonsCamera}
                    {stage === "preview" && ButtonsPreview}
                  </Grid>
                </Grid>

                <Grid size={12 as any}>
                  <Grid container justifyContent="center">
                    <Button
                      onClick={goBack}
                      variant="outlined"
                      color="secondary"
                      fullWidth
                      disabled={loading}
                      size="large"
                      sx={{ mt: 2 }}
                    >
                      <strong>Back</strong>
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Container>
      <BrandLoader open={loading} />
    </>
  );
};

export default Scanner;
